/**
 * Workflow de envío desde bodega (post-DTE): stock consolidado + `war_guide` (listado unificado).
 *
 * Flujo:
 * 1. Esperar PDF del DTE consolidado
 * 2. Subir PDF al gestor documental
 * 3. `warehouseSendFromStock` (residue)
 * 4. `createWarehouseGuide` (mismo payload que RegisterWorkflow vía buildResiduosWarehouseGuidePayload)
 */

import { warehouseSendFromStock } from '../../../../../slices/guideDetail/thunk';
import {
	readDTEFacele,
	createWarehouseGuide,
} from '../../../../../slices/warehouseGuide/thunk';
import {
	uploadFoldersFile,
	getFileTypeByCode,
} from '../../../../../slices/thunks';
import {
	waitForPDF,
	uploadDocument,
	generateFileName,
} from '../utils/pdfWorkflowUtils';
import { FOLDER_PATHS } from '../../constants/guideConstants';
import { mergeObservacionesGuiaForPayload } from '../../utils/residuosObservacionesAuto';
import {
	buildResiduosGuideDetailsFromFormItems,
	buildResiduosWarehouseGuidePayload,
} from '../../utils/buildResiduosWarehouseGuidePayload';

export class ResiduosWarehouseStockSendWorkflow {
	/**
	 * @param {Function} dispatch
	 * @param {Object} dteResult
	 * @param {Object} formValues
	 * @param {Function} [onProgress] (stepId, status, message?)
	 */
	static async execute(dispatch, dteResult, formValues, onProgress) {
		const dteId = dteResult?.data?._id;
		const folio = dteResult?.data?.folio || '';

		let documentId = null;
		let resGuideId = null;
		let warGuideId = null;

		try {
			if (onProgress) onProgress('wf_pdf', 'loading');

			const pdfBlob = await waitForPDF(dispatch, dteId, readDTEFacele);
			if (!pdfBlob) {
				if (onProgress)
					onProgress('wf_pdf', 'error', 'No se pudo obtener el PDF');
				const error = new Error(
					'No se pudo obtener el PDF de la guía consolidada.',
				);
				error.createdResources = {
					resGuideId,
					warGuideId,
					documentId,
					folio,
				};
				throw error;
			}
			if (onProgress) onProgress('wf_pdf', 'done');

			if (onProgress) onProgress('wf_upload', 'loading');
			const fileName = generateFileName(dteResult, 'RES-CONSOL');
			documentId = await uploadDocument(dispatch, pdfBlob, fileName, {
				establishmentId: formValues.establishment_id,
				folderPath:
					FOLDER_PATHS.RESIDUOS_CONSOLIDADAS ||
					'/Residuos/Guias/Consolidadas',
				getFileTypeByCodeThunk: getFileTypeByCode,
				uploadFoldersFileThunk: uploadFoldersFile,
			});

			if (!documentId) {
				if (onProgress)
					onProgress('wf_upload', 'error', 'Error al subir documento');
				const error = new Error(
					'No se pudo subir el documento de la guía consolidada.',
				);
				error.createdResources = {
					resGuideId,
					warGuideId,
					documentId,
					folio,
				};
				throw error;
			}
			if (onProgress) onProgress('wf_upload', 'done');

			if (onProgress) onProgress('wf_consolidate', 'loading');

			const stockSlots = [];

			formValues.items.forEach(item => {
				const moduleVars = item._module_vars || {};
				if (moduleVars._is_warehouse_stock_send) {
					stockSlots.push({
						warehouse_id: moduleVars.warehouse_id,
						residue_id: moduleVars.residue_id,
						kilograms_to_send: Number(
							moduleVars.kilograms_to_send || item.QtyItem || 0,
						),
					});
				}
			});

			if (!stockSlots.length) {
				const error = new Error(
					'No hay stock RESPEL seleccionado para enviar.',
				);
				error.createdResources = {
					resGuideId,
					warGuideId,
					documentId,
					folio,
				};
				throw error;
			}

			const sendData = {
				data_guide: {
					dte_id: dteId,
					folio: parseInt(folio, 10),
					document_id: documentId,
					guide_date: formValues.guide_date,
					establishment_id: formValues.establishment_id,
					responsible_id: formValues.responsible_id,
					residue_type_id: formValues.residue_type_id,
					destiny_type_id: parseInt(formValues.destiny_type_id, 10),
					warehouse_id:
						formValues.destiny_type_id === '1'
							? formValues.warehouse_id
							: '',
					final_destination_id:
						formValues.destiny_type_id === '2'
							? formValues.final_destination_id
							: '',
					transfer_category_id: formValues.transfer_category_ids?.[0] || '',
					patente_id: formValues.patente_id,
					matricula_id: formValues.matricula_id || '',
					observation: mergeObservacionesGuiaForPayload(formValues),
				},
				data_management_stock: {
					stock_slots: stockSlots,
				},
			};

			try {
				const result = await dispatch(
					warehouseSendFromStock(sendData),
				).unwrap();
				resGuideId = result.guide?._id;
				if (onProgress) onProgress('wf_consolidate', 'done');
			} catch (consolidateError) {
				if (onProgress)
					onProgress(
						'wf_consolidate',
						'error',
						consolidateError.message || 'Error al consolidar',
					);
				const error = new Error(
					consolidateError.message ||
						'Error al consolidar guías de bodega',
				);
				error.createdResources = {
					resGuideId,
					warGuideId,
					documentId,
					folio,
				};
				throw error;
			}

			const guideDetails = buildResiduosGuideDetailsFromFormItems(
				formValues.items,
			);

			if (onProgress) onProgress('wf_warehouse', 'loading');
			const warehouseGuideData = buildResiduosWarehouseGuidePayload({
				dteId,
				folio,
				documentId,
				formValues,
				resGuideId,
				guideDetails,
			});

			try {
				const warehouseGuideResult = await dispatch(
					createWarehouseGuide(warehouseGuideData),
				).unwrap();
				warGuideId = warehouseGuideResult._id;
				if (onProgress) onProgress('wf_warehouse', 'done');
			} catch (whError) {
				console.error(
					'[ResiduosWarehouseStockSendWorkflow] Error createWarehouseGuide:',
					whError,
				);
				if (onProgress)
					onProgress(
						'wf_warehouse',
						'error',
						whError.message || 'Error al registrar en listado unificado',
					);
				const error = new Error(
					whError.message || 'Error al registrar en el listado unificado',
				);
				error.createdResources = {
					resGuideId,
					warGuideId,
					documentId,
					folio,
				};
				throw error;
			}

			return {
				success: true,
				guideId: resGuideId,
				folio: folio,
				documentId: documentId,
				createdResources: {
					resGuideId: resGuideId,
					warGuideId: warGuideId,
					documentId: documentId,
					folio: folio,
				},
			};
		} catch (error) {
			console.error('[ResiduosWarehouseStockSendWorkflow] Error:', error);
			if (!error.createdResources) {
				error.createdResources = {
					resGuideId,
					warGuideId,
					documentId,
					folio,
				};
			}
			throw error;
		}
	}
}

export default ResiduosWarehouseStockSendWorkflow;
